// src/components/LoadingTransition.tsx
import { Box, Fade, keyframes } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import conectando2 from '../../assets/Img/dti.png';
import { getHomeRoute } from '../../hooks/useHomeRedirect';

// Animação de pulsação suave
const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// Animação de fade in/out para os pontos
const dotPulse = keyframes`
  0%, 100% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
`;

// Animação de rotação
const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const LoadingTransition: React.FC = () => {
  const history = useHistory();
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    setShowContent(true);

    const timer = setTimeout(() => {
      const homeRoute = getHomeRoute();
      history.replace(homeRoute);
    }, 5000);

    return () => clearTimeout(timer);
  }, [history]);

  return (
    <Fade in timeout={500}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: 'background.default',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Círculo de fundo animado */}
        <Box
          sx={{
            position: 'absolute',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(25, 118, 210, 0.08) 0%, transparent 70%)',
            animation: `${pulse} 3s ease-in-out infinite`,
          }}
        />

        {/* Container centralizado do conteúdo */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
            position: 'relative',
            zIndex: 1,
          }}
        >
          {/* Logo/Imagem com animação suave */}
          <Fade in={showContent} timeout={1000}>
            <Box
              component="img"
              src={conectando2}
              alt="Carregando..."
              sx={{
                width: '75%',
                maxWidth: '500px',
                objectFit: 'contain',
                animation: `${pulse} 2s ease-in-out infinite`,
                filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.1))',
              }}
            />
          </Fade>

          {/* Indicador de loading - pontos animados */}
          <Fade in={showContent} timeout={1500}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
              }}
            >
              {[0, 1, 2].map((index) => (
                <Box
                  key={index}
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    animation: `${dotPulse} 1.4s ease-in-out infinite`,
                    animationDelay: `${index * 0.2}s`,
                  }}
                />
              ))}
            </Box>
          </Fade>

          {/* Spinner circular opcional */}
          <Box
            sx={{
              width: 40,
              height: 40,
              border: '3px solid',
              borderColor: 'rgba(25, 118, 210, 0.1)',
              borderTopColor: 'primary.main',
              borderRadius: '50%',
              animation: `${rotate} 1s linear infinite`,
              mt: 1,
            }}
          />
        </Box>
      </Box>
    </Fade>
  );
};

export default LoadingTransition;
